import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

export const setArchitectKey = (key) => {
  if (key) client.defaults.headers["X-Architect-Key"] = key;
  else delete client.defaults.headers["X-Architect-Key"];
};

const ls = typeof window !== "undefined" ? window.localStorage : null;
if (ls && ls.getItem("nx_architect_key")) {
  setArchitectKey(ls.getItem("nx_architect_key"));
}

// ── Anonymous session handshake (SEC-001) ─────────────────────────────
// Free HMAC token fetched transparently before chat. Cached until expiry.
const SESSION_TOKEN_KEY = "nx_session_token_v1";

async function ensureChatSession() {
  try {
    const raw = ls?.getItem(SESSION_TOKEN_KEY);
    if (raw) {
      const { token, expiresAt } = JSON.parse(raw);
      if (token && new Date(expiresAt).getTime() > Date.now() + 60_000) return token;
    }
  } catch { /* re-handshake */ }
  const r = await client.post("/chat/handshake", {});
  const token = r.data.session_token;
  try {
    ls?.setItem(SESSION_TOKEN_KEY, JSON.stringify({ token, expiresAt: r.data.expires_at }));
  } catch { /* noop */ }
  return token;
}

export const api = {
  stats: () => client.get("/stats").then((r) => r.data),
  health: () => client.get("/healthz").then((r) => r.data),
  nodes: () => client.get("/nodes").then((r) => r.data),
  // Sovereign Peer systems (confer-only, not satellites)
  peers: () => client.get("/peers").then((r) => r.data),
  peer: (id) => client.get(`/peers/${id}`).then((r) => r.data),
  rotateNode: (id) => client.post(`/nodes/${id}/rotate-key`).then((r) => r.data),
  ledger: (params) => client.get("/ledger", { params }).then((r) => r.data),
  context: (params) => client.get("/ledger/context", { params }).then((r) => r.data),
  writeLedger: (body, nodeKey) =>
    client
      .post("/ledger/write", body, nodeKey ? { headers: { "X-Node-Key": nodeKey } } : undefined)
      .then((r) => r.data),
  directives: (params) => client.get("/directives", { params }).then((r) => r.data),
  deliberations: (params) => client.get("/deliberations", { params }).then((r) => r.data),
  execute: (body, nodeKey) =>
    client
      .post("/execute", body, nodeKey ? { headers: { "X-Node-Key": nodeKey } } : undefined)
      .then((r) => r.data),
  settings: () => client.get("/settings").then((r) => r.data),
  updateSettings: (body) => client.put("/settings", body).then((r) => r.data),
  seed: () => client.post("/seed").then((r) => r.data),
  integrity: () => client.get("/system/integrity").then((r) => r.data),
  revive: () => client.post("/system/revive").then((r) => r.data),
  evidencePreview: () => client.get("/evidence/preview").then((r) => r.data),
  vessels: () => client.get("/vessels").then((r) => r.data),
  benchmarkVessels: (vessel_id) =>
    client.post("/vessels/benchmark", null, vessel_id ? { params: { vessel_id } } : undefined).then((r) => r.data),
  spawnVessel: (body) => client.post("/vessels/spawn", body).then((r) => r.data),
  sealVessel: (id) => client.post(`/vessels/seal/${id}`).then((r) => r.data),
  pubkey: () => client.get("/codex/pubkey").then((r) => r.data),
  keygen: (passphrase) => client.post("/codex/keygen", { passphrase }).then((r) => r.data),
  unloadKey: () => client.post("/codex/unload").then((r) => r.data),
  verifySignature: (body) => client.post("/codex/verify", body).then((r) => r.data),
  evidenceBuildUrl: () => `${API}/evidence/build`,
  // ── KEYS Library ──
  libraryStats: () => client.get("/library/stats").then((r) => r.data),
  librarySearch: (params) => client.get("/library/search", { params }).then((r) => r.data),
  libraryGet: (id) => client.get(`/library/nodes/${id}`).then((r) => r.data),
  libraryIngest: (body) => client.post("/library/ingest", body).then((r) => r.data),
  libraryDelete: (id) => client.delete(`/library/nodes/${id}`).then((r) => r.data),
  libraryCatalog: () => client.get("/library/commercial").then((r) => r.data),
  librarySetCommercial: (id, body) => client.patch(`/library/nodes/${id}/commercial`, body).then((r) => r.data),
  libraryLicenseProof: (id) => client.get(`/library/nodes/${id}/license-proof`).then((r) => r.data),
  // Self-Adaptive Truth Engine
  truthEngineState: () => client.get("/truth-engine/state").then((r) => r.data),
  // Tokenized redemption (legacy M-D-KEYWORD)
  licensesActive: () => client.get("/licenses/active").then((r) => r.data),
  licensesRedeem: (body) => client.post("/licenses/redeem", body).then((r) => r.data),
  // Gumroad License Verify · $5=1 token · Architect 2026-02
  redeemLicense: (license_key) => client.post("/redeem", { license_key }).then((r) => r.data),
  walletBalance: (license_key) => client.get(`/wallet/balance/${encodeURIComponent(license_key)}`).then((r) => r.data),
  walletAddresses: () => client.get("/wallet").then((r) => r.data),
  productsCatalog: () => client.get("/products").then((r) => r.data),
  executeToken: (body) => client.post("/execute", body).then((r) => r.data),
  heartbeat: () => client.get("/heartbeat").then((r) => r.data),
  // Resend domain verification status (Architect Console badge)
  resendStatus: () => client.get("/domains/resend").then((r) => r.data),
  // Satellite heartbeat (iframe chapels ping on mount)
  satelliteHeartbeat: (body) => client.post("/satellite/heartbeat", body).then((r) => r.data),
  // Satellite presence widget (Architect Console)
  satellitePresence: (hours = 24) =>
    client.get("/satellite/presence", { params: { window_hours: hours } }).then((r) => r.data),
  // Federation Seeds (sovereign successors, dormant by default)
  seeds: () => client.get("/seeds").then((r) => r.data),
  seed: (id) => client.get(`/seeds/${id}`).then((r) => r.data),
  // Architect-only · mint a seed activation key
  mintSeedKey: (id, archKey) =>
    client.post(`/seeds/${id}/mint-key`, {}, { headers: { "X-Architect-Key": archKey } }).then((r) => r.data),
  // Universal Cockpit chat
  chatPersonas: () => client.get("/chat/personas").then((r) => r.data),
  // Member auth status (200 always — sign-in state + linked credit balance)
  authStatus: () => client.get("/auth/status", { withCredentials: true }).then((r) => r.data),
  chatSessions: (travelerId) =>
    client.get("/chat/sessions", { headers: { "X-Traveler-Id": travelerId } }).then((r) => r.data),
  xaiRealtimeToken: async () => {
    const headers = { "X-Session-Token": await ensureChatSession() };
    return client.post("/xai/realtime/token", {}, { headers }).then((r) => r.data);
  },

  deepseekStatus: () => client.get("/providers/deepseek/status").then((r) => r.data),

  deepseekQuery: (messages) =>
    client.post("/providers/deepseek/query", { messages }).then((r) => r.data),

  chatSend: async (body, travelerId, architectKey) => {
    const headers = { "X-Traveler-Id": travelerId };
    if (architectKey) headers["X-Architect-Key"] = architectKey;
    headers["X-Session-Token"] = await ensureChatSession();
    try {
      const r = await client.post("/chat/messages", body, { headers, withCredentials: true });
      return r.data;
    } catch (e) {
      if (e?.response?.status === 401) {
        try { ls?.removeItem(SESSION_TOKEN_KEY); } catch { /* noop */ }
        headers["X-Session-Token"] = await ensureChatSession();
        const r2 = await client.post("/chat/messages", body, { headers, withCredentials: true });
        return r2.data;
      }
      throw e;
    }
  },
  chatMessages: (sessionId, travelerId) =>
    client.get("/chat/messages", { params: { session_id: sessionId }, headers: { "X-Traveler-Id": travelerId } }).then((r) => r.data),
  bridgePackAll: () => client.get("/federation/bridge-pack").then((r) => r.data),
  bridgePack: (nodeId) => client.get(`/federation/bridge-pack/${nodeId}`).then((r) => r.data),
  // Vessel watcher (auto-spawn when active vessel is full)
  watcherStatus: () => client.get("/vessels/watcher").then((r) => r.data),
  watcherRunOnce: () => client.post("/vessels/watcher/run-once").then((r) => r.data),
  watcherEnable: () => client.post("/vessels/watcher/enable").then((r) => r.data),
  watcherDisable: () => client.post("/vessels/watcher/disable").then((r) => r.data),
  // Analytics
  lensDrift: (limit = 50) => client.get("/deliberations/lens-drift", { params: { limit } }).then((r) => r.data),
  // Phone Booth · Passkey
  passkeySend: (body) => client.post("/passkey/send", body).then((r) => r.data),
  passkeyVerify: (body) => client.post("/passkey/verify", body).then((r) => r.data),
  passkeyQuota: (token) =>
    client.get("/passkey/quota", { headers: { "X-Passkey-Token": token } }).then((r) => r.data),
  passkeyConsume: (token) =>
    client.post("/passkey/consume", null, { headers: { "X-Passkey-Token": token } }).then((r) => r.data),
  // Purchase
  purchaseIntent: (nodeId, buyerEmail, passkeyToken) => {
    const headers = passkeyToken ? { "X-Passkey-Token": passkeyToken } : {};
    return client
      .post(`/library/nodes/${nodeId}/purchase-intent`, null, {
        params: buyerEmail ? { buyer_email: buyerEmail } : undefined,
        headers,
      })
      .then((r) => r.data);
  },
  // Gumroad
  gumroadClaim: (nodeId, licenseKey, buyerEmail) =>
    client
      .post(`/library/nodes/${nodeId}/claim-gumroad`, {
        license_key: licenseKey,
        buyer_email: buyerEmail,
      })
      .then((r) => r.data),
  gumroadAttach: (nodeId, gumroadUrl, gumroadPermalink) =>
    client
      .patch(`/library/nodes/${nodeId}/gumroad`, {
        gumroad_url: gumroadUrl,
        gumroad_permalink: gumroadPermalink,
      })
      .then((r) => r.data),
  evidenceDownload: async () => {
    const res = await client.post("/evidence/build", null, { responseType: "blob" });
    const dispo = res.headers["content-disposition"] || "";
    const match = /filename="([^"]+)"/.exec(dispo);
    const filename = match ? match[1] : `EXPORT_FOR_SUPPORT.zip`;
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return filename;
  },
  // ── Prompt Federation (free engagement layer)
  promptFederationIndex: () =>
    client.get("/prompt-federation/index").then((r) => r.data),
  promptFederationManual: () =>
    client.get("/prompt-federation/manual", { responseType: "text" }).then((r) => r.data),
  promptFederationAssemble: (stage, question, perspectives) =>
    client
      .post("/prompt-federation/assemble", { stage, question, perspectives })
      .then((r) => r.data),
};
